import { Outlet, Link, useLocation, useNavigate } from "react-router";
import { Home, Receipt, BarChart3, BarChart2, Settings, Bell, Plus, Zap, LogOut, ShoppingBag, ShoppingCart, Share2, Camera, Wallet, Users, Ticket, Package, ShieldCheck, Handshake, PiggyBank } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "../../lib/auth";
import { useNotifications } from "../../hooks/useNotifications";
import { useSubscription } from "../../hooks/useSubscription";
import { OnboardingTour } from "../components/OnboardingTour";
import { BugReportModal } from "../components/BugReportModal";
import { useState } from "react";
import { Bug } from "lucide-react";
import { toast } from "sonner";
import { CNPLComingSoonModal } from "../components/seller/CNPLComingSoonModal";
import { useHaptics } from "../../hooks/useHaptics";
import { PullToRefresh } from "../components/ui/PullToRefresh";
import { RouteErrorBoundary } from "../components/ui/ErrorBoundary";

const navItems = [
  {
    group: "Main", items: [
      { to: "/seller", label: "Dashboard", icon: Home, exact: true },
      { id: "tour-products", to: "/seller/products", label: "Products", icon: ShoppingBag, exact: false },
      { id: "tour-coupons", to: "/seller/coupons", label: "Coupons", icon: Ticket, exact: false },
      { id: "tour-orders", to: "/seller/orders", label: "Orders", icon: Receipt, exact: false },
      { id: "tour-customers", to: "/seller/customers", label: "Customers", icon: Users, exact: false },
      { id: "tour-abandoned-carts", to: "/seller/abandoned-carts", label: "Abandoned Carts", icon: ShoppingCart, exact: false },
      { to: "/seller/leads", label: "Leads", icon: Users, exact: false },
      { id: "nav-wallet", to: "/seller/wallet", label: "Wallet", icon: Wallet, exact: false },
      { to: "/seller/passport", label: "Financial Passport", icon: ShieldCheck, exact: false },
      { to: "/seller/cnpl", label: "Peer Credit", icon: Handshake, exact: false },
      { to: "/seller/statuses", label: "Statuses", icon: Camera, exact: false },
    ]
  },
  {
    group: "Reports", items: [
      { id: "tour-analytics", to: "/seller/reports", label: "Analytics", icon: BarChart3, exact: false },
      { id: "storefront-analytics", to: "/seller/analytics", label: "Storefront Stats", icon: BarChart2, exact: false },
      { id: "tour-expenses", to: "/seller/expenses", label: "Expenses", icon: PiggyBank, exact: false },
    ]
  },
  {
    group: "Account", items: [
      { id: "tour-settings", to: "/seller/settings", label: "Settings", icon: Settings, exact: false },
    ]
  },
  {
    group: "Admin", items: [
      { id: "admin-catalog", to: "/seller/admin/catalog", label: "Catalog", icon: Package, exact: false },
      { id: "admin-sellers", to: "/seller/admin/sellers", label: "Sellers", icon: Users, exact: false },
    ]
  },
];

const ADMIN_EMAILS = ["admin@moniflow.shop"];

export function SellerLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, user, signOut } = useAuth();
  const { unreadCount } = useNotifications();
  const { isActive, loading: subLoading, subscription } = useSubscription();
  const haptics = useHaptics();

  const handleSignOut = async () => {
    haptics('heavy');
    await signOut();
    window.location.href = '/seller/login';
  };

  const displayName = profile?.full_name ?? user?.email ?? 'User';
  const initials = displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isActiveRoute = (to: string, exact: boolean) => {
    if (exact) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  const trialDaysLeft = (() => {
    const started = profile?.trial_started_at ?? profile?.created_at;
    if (!started) return 60;
    const daysSince = Math.floor((Date.now() - new Date(started).getTime()) / 86_400_000);
    return Math.max(0, 60 - daysSince);
  })();

  const showUpgradeCard = !subLoading && !isActive;
  const planName = isActive ? (subscription?.plan === 'pro' ? 'Pro Plan' : 'Basic Plan') : 'Basic (Trial)';

  const [restartTour, setRestartTour] = useState(0);
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [showCNPLSoonModal, setShowCNPLSoonModal] = useState(false);

  // Filter primary items for bottom nav
  const primaryNavItems = [
    { to: "/seller", label: "Dashboard", icon: Home, exact: true },
    { to: "/seller/products", label: "Products", icon: ShoppingBag, exact: false },
    { to: "/seller/orders", label: "Orders", icon: Receipt, exact: false },
    { to: "/seller/customers", label: "Customers", icon: Users, exact: false },
  ];

  const secondaryNavItems = [
    { to: "/seller/abandoned-carts", label: "Ab. Carts", icon: ShoppingCart, exact: false },
    { to: "/seller/leads", label: "Leads", icon: Users, exact: false },
    { to: "/seller/statuses", label: "Statuses", icon: Camera, exact: false },
    { to: "/seller/reports", label: "Analytics", icon: BarChart3, exact: false },
    { to: "/seller/analytics", label: "Storefront", icon: BarChart2, exact: false },
    { to: "/seller/expenses", label: "Expenses", icon: PiggyBank, exact: false },
    { to: "/seller/wallet", label: "Wallet", icon: Wallet, exact: false },
    ...(user?.email && ADMIN_EMAILS.includes(user.email) ? [{ to: "/seller/admin/catalog", label: "Catalog", icon: Package, exact: false }] : []),
    { to: "/seller/settings", label: "Settings", icon: Settings, exact: false },
  ];

  return (
    <div className="min-h-screen bg-[#F3F4F6] pb-24 md:pb-0 font-sans selection:bg-[#0B6E4F]/10 selection:text-[#0B6E4F] ui-element no-tap-highlight">
      {/* <OnboardingTour key={restartTour} forceStart={restartTour > 0} /> */}

      {/* ── Mobile Top Bar ────────────────────────────────────── */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-2xl z-40 h-16 shadow-sm border-b border-[#0D1A12]/5 px-5 flex items-center justify-between safe-top">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0B6E4F] flex items-center justify-center shadow-lg shadow-[#0B6E4F]/20">
              <span className="text-white font-bold text-base">M</span>
            </div>
            <div className="flex flex-col">
              <span className="font-black text-base text-[#0D1A12] leading-none">MoniFlow</span>
              <span className="text-[10px] text-[#0B6E4F] font-bold uppercase tracking-wider mt-0.5">Seller Hub</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Link
              to="/seller/notifications"
              onClick={() => haptics('light')}
              className="relative w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[#F3F4F6] transition-colors"
            >
              <Bell className="w-5 h-5 text-[#374151]" />
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2 min-w-[16px] h-4 bg-[#EF4444] rounded-full ring-2 ring-white flex items-center justify-center animate-in zoom-in duration-300">
                  <span className="text-[8px] font-black text-white">{unreadCount > 9 ? '9+' : unreadCount}</span>
                </span>
              )}
            </Link>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0B6E4F] to-[#065F46] flex items-center justify-center text-white font-black text-xs ring-4 ring-[#F1F3F5] shadow-sm ml-1 active:scale-90 transition-transform" onClick={() => haptics('medium')}>
              {initials}
            </div>
          </div>
      </div>

      {/* ── Desktop Sidebar ────────────────────────────────────── */}
      <aside id="tour-sidebar" className="hidden md:flex flex-col fixed left-0 top-0 bottom-0 w-64 bg-[#0D1A12] z-50">
        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#0B6E4F] flex items-center justify-center shadow-lg shadow-[#0B6E4F]/30">
              <span className="text-white font-bold text-lg leading-none">M</span>
            </div>
            <span className="font-bold text-xl text-white tracking-tight">MoniFlow</span>
          </div>
          <Link
            to="/seller/notifications"
            className="relative w-9 h-9 flex items-center justify-center rounded-xl text-white/40 hover:text-white hover:bg-white/5 transition-all"
          >
            <Bell className="w-4.5 h-4.5" />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[14px] h-3.5 bg-[#EF4444] rounded-full ring-2 ring-[#0D1A12] flex items-center justify-center animate-in zoom-in duration-300">
                <span className="text-[7px] font-black text-white">{unreadCount > 9 ? '9+' : unreadCount}</span>
              </span>
            )}
          </Link>
        </div>

        {/* New Order shortcut */}
        <div id="tour-new-order" className="px-4 pt-5 pb-2">
          <Link
            to="/seller/orders/new"
            className="flex items-center justify-center gap-2 w-full bg-[#0B6E4F] hover:bg-[#13A463] text-white py-2.5 rounded-xl text-sm font-semibold transition-all shadow-lg shadow-[#0B6E4F]/20 group"
          >
            <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
            New Order
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
          {navItems
            .filter(group => group.group !== "Admin" || (user?.email && ADMIN_EMAILS.includes(user.email)))
            .map(({ group, items }) => (
              <div key={group}>
                <div className="text-[10px] font-bold text-white/30 uppercase tracking-widest px-3 mb-2">
                  {group}
                </div>
                <div className="space-y-0.5">
                  {items.map(({ id, to, label, icon: Icon, exact }) => {
                      const active = isActiveRoute(to, exact);
                      const isWallet = id === "nav-wallet" || label === "Wallet";
                      const isCNPL = to === "/seller/cnpl" || label === "Peer Credit";
                      const isSoon = isWallet || id === "tour-expenses" || isCNPL;
                      const showSoonBadge = isSoon && (import.meta as any).env.PROD;

                      const handleClick = (e: React.MouseEvent) => {
                        if (showSoonBadge) {
                          e.preventDefault();
                          if (isCNPL) {
                            setShowCNPLSoonModal(true);
                          } else {
                            toast.info("We are launching soon! 🚀", {
                              description: "This feature is currently in final testing."
                            });
                          }
                        }
                      };

                      return (
                        <Link
                          id={id}
                          key={to}
                          to={showSoonBadge ? location.pathname : to}
                          onClick={handleClick}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 group ${active
                            ? "bg-[#0B6E4F] text-white shadow-lg shadow-[#0B6E4F]/30"
                            : "text-white/60 hover:text-white hover:bg-white/5 hover:translate-x-1"
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-4.5 h-4.5 flex-shrink-0" strokeWidth={active ? 2.5 : 2} />
                            {label}
                          </div>

                          {label === "Financial Passport" && !active && (
                            <span className="text-[8px] bg-[#0B6E4F] text-white px-2 py-0.5 rounded-md font-black uppercase tracking-widest animate-pulse ml-auto">
                              New
                            </span>
                          )}

                          {showSoonBadge && (
                            <span className="text-[10px] bg-[#FEF3C7] text-[#B45309] px-2 py-0.5 rounded-full font-black uppercase tracking-tighter shadow-sm border border-[#FDE68A] ml-auto">
                              Soon
                            </span>
                          )}
                        </Link>
                      );
                  })}
                </div>
              </div>
            ))}
        </nav>

        {/* User profile */}
        <div className="px-3 pb-5 border-t border-white/5 pt-4">
          {showUpgradeCard && (
            <Link
              to="/seller/subscription"
              className="flex items-center gap-2.5 bg-gradient-to-r from-[#0B6E4F]/30 to-transparent border border-[#0B6E4F]/40 rounded-[1.5rem] px-3 py-2.5 mb-3 hover:border-[#0B6E4F]/60 transition-all hover:scale-[1.02] group"
            >
              <Zap className="w-4 h-4 text-[#34D399] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white/80 group-hover:text-white">
                  {trialDaysLeft > 0 ? `${trialDaysLeft} trial days left` : "Trial expired"}
                </div>
                <div className="text-[10px] text-[#34D399] font-medium">Upgrade to Pro →</div>
              </div>
            </Link>
          )}

          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0B6E4F] to-[#065F46] flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ring-2 ring-white/10">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{displayName}</div>
                <div className="text-xs text-white/40">{profile?.business_name ?? planName}</div>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              title="Sign out"
              className="p-2 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition-all"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="md:ml-64 mt-16 md:mt-0 min-h-[calc(100vh-64px)] md:min-h-screen relative">
        <PullToRefresh onRefresh={async () => {
          // Trigger a global refresh/refetch if needed, or just a small delay for feel
          await new Promise(resolve => setTimeout(resolve, 1500));
        }}>
          <RouteErrorBoundary resetKey={location.pathname}>
            <AnimatePresence>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                className="w-full h-full"
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </RouteErrorBoundary>
        </PullToRefresh>
      </main>

      {/* ── Mobile More Menu Overlay ───────────────────────────── */}
      <AnimatePresence>
        {isMoreMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#0D1A12]/60 backdrop-blur-sm"
              onClick={() => setIsMoreMenuOpen(false)}
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                if (info.offset.y > 100) {
                  setIsMoreMenuOpen(false);
                  haptics('light');
                }
              }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[2.5rem] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
            >
              <div className="p-4 flex flex-col items-center border-b border-[#F1F3F5] bg-white sticky top-0 z-10">
                <div className="w-12 h-1.5 bg-[#E5E7EB] rounded-full mb-6" />
                <div className="flex items-center justify-between w-full px-4">
                  <h3 className="font-extrabold text-xl text-[#0D1A12] tracking-tight">Management</h3>
                  <button
                    onClick={() => {
                      setIsMoreMenuOpen(false);
                      haptics('light');
                    }}
                    className="w-10 h-10 rounded-full bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF] active:scale-90 transition-all"
                  >
                    <Plus className="w-5 h-5 rotate-45" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 p-6 overflow-y-auto flex-1">
                {secondaryNavItems.map(({ to, label, icon: Icon, exact }) => {
                  const active = isActiveRoute(to, exact);
                  const isWallet = label === "Wallet";
                  const isSoon = isWallet;
                  const showSoonBadge = isSoon && (import.meta as any).env.PROD;

                  const handleClick = (e: React.MouseEvent) => {
                    haptics('light');
                    if (showSoonBadge) {
                      e.preventDefault();
                      setIsMoreMenuOpen(false);
                      toast.info("We are launching soon! 🚀");
                    } else {
                      setIsMoreMenuOpen(false);
                    }
                  };

                  return (
                    <Link
                      key={to}
                      to={showSoonBadge ? location.pathname : to}
                      onClick={handleClick}
                      className={`flex flex-col items-center justify-center gap-3 p-6 rounded-3xl border transition-all active:scale-95 ${active
                          ? "bg-[#0B6E4F] border-[#0B6E4F] text-white shadow-xl shadow-[#0B6E4F]/20"
                          : "bg-white border-[#F1F3F5] text-[#64748B]"
                        }`}
                    >
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-1 ${active ? 'bg-white/10' : 'bg-[#F3F4F6] text-[#0B6E4F]'}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="text-sm font-black tracking-tight">{label}</span>
                      {showSoonBadge && (
                        <span className="text-[8px] bg-[#FEF3C7] text-[#B45309] px-2 py-0.5 rounded-full font-black uppercase border border-[#FDE68A]">
                          Soon
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
              <div className="p-4 pt-0 safe-bottom">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl bg-red-50 text-red-600 font-bold text-sm active:bg-red-100 transition-all"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Mobile Bottom Nav ──────────────────────────────────── */}
      <nav className="md:hidden fixed bottom-6 left-6 right-6 bg-white/90 backdrop-blur-[32px] z-40 shadow-2xl shadow-emerald-900/20 rounded-[2.5rem] px-2 py-1 border border-white/50 animate-in slide-in-from-bottom-12 duration-1000 ease-[0.23,1,0.32,1]">
        <div className="flex h-16 items-center justify-around max-w-lg mx-auto">
          {primaryNavItems.map(({ to, label, icon: Icon, exact }) => {
            const active = isActiveRoute(to, exact);
            return (
              <Link
                key={to}
                to={to}
                onClick={() => haptics('light')}
                className={`flex flex-col items-center justify-center gap-1.5 flex-1 min-h-[44px] relative transition-all duration-300 ${active ? "text-[#0B6E4F]" : "text-[#9CA3AF]"
                  }`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-full relative z-10 transition-all duration-300 ${active ? 'scale-110' : 'hover:bg-gray-50'
                  }`}>
                  {active && (
                    <motion.div
                      layoutId="active-nav-bg"
                      className="absolute inset-0 bg-[#0B6E4F]/10 rounded-full"
                      transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
                    />
                  )}
                  <Icon className="w-5 h-5 relative z-10" strokeWidth={active ? 2.5 : 2} />
                </div>
                <span className={`text-[8px] font-black uppercase tracking-[0.15em] leading-none transition-all duration-300 ${active ? 'opacity-100' : 'opacity-40'
                  }`}>
                  {label}
                </span>
                {active && (
                   <motion.div 
                     layoutId="active-indicator"
                     className="absolute -bottom-1 w-1 h-1 bg-[#0B6E4F] rounded-full"
                     transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                   />
                )}
              </Link>
            );
          })}

          <button
            onClick={() => {
              setIsMoreMenuOpen(true);
              haptics('medium');
            }}
            className={`flex flex-col items-center justify-center gap-1.5 flex-1 min-h-[44px] relative transition-all duration-300 ${isMoreMenuOpen ? "text-[#0B6E4F]" : "text-[#9CA3AF]"
              }`}
          >
            <div className={`w-11 h-11 flex items-center justify-center rounded-full transition-all duration-500 ease-[0.23,1,0.32,1] ${isMoreMenuOpen ? 'bg-[#0B6E4F] text-white rotate-45 scale-110 shadow-lg shadow-[#0B6E4F]/40' : 'bg-gray-100/50 text-[#9CA3AF] hover:bg-gray-100'
              }`}>
              <Plus className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <span className={`text-[9px] font-black uppercase tracking-[0.1em] leading-none transition-all duration-300 ${isMoreMenuOpen ? 'opacity-100' : 'opacity-40'
              }`}>
              More
            </span>
          </button>
        </div>
      </nav>


      {/* Mobile FAB */}
      {location.pathname === "/seller" && (
        <Link
          to="/seller/orders/new"
          onClick={() => haptics('medium')}
          className="md:hidden fixed bottom-28 right-6 w-14 h-14 bg-[#0B6E4F] rounded-2xl shadow-2xl shadow-[#0B6E4F]/40 flex items-center justify-center z-50 active:scale-90 transition-all border-2 border-white/20 animate-in zoom-in-50 duration-500 delay-300 safe-bottom"
        >
          <Plus className="w-6 h-6 text-white" />
        </Link>
      )}

      {/* Bug Report - Floating Button & Modal */}
      {location.pathname === "/seller" && (
        <button
          onClick={() => {
            setIsBugModalOpen(true);
            haptics('light');
          }}
          className="fixed bottom-[90px] md:bottom-6 left-4 md:left-auto md:right-6 w-10 h-10 md:w-12 md:h-12 bg-white/90 backdrop-blur-md border border-[#E5E7EB] rounded-2xl shadow-lg flex items-center justify-center text-[#9CA3AF] hover:text-[#0B6E4F] hover:border-[#0B6E4F]/30 hover:bg-white transition-all z-50 group safe-bottom"
          title="Report a Bug"
        >
          <Bug className="w-4 h-4 md:w-4.5 md:h-4.5 group-hover:scale-110 transition-transform" />
        </button>
      )}

      <BugReportModal
        isOpen={isBugModalOpen}
        onClose={() => setIsBugModalOpen(false)}
      />

      <CNPLComingSoonModal 
        isOpen={showCNPLSoonModal} 
        onClose={() => setShowCNPLSoonModal(false)} 
      />
    </div>
  );
}
